import React, { useState, useEffect, useRef } from 'react';
import { Bot, Send, User, RefreshCw, AlertCircle, Headphones, Plus, MessageSquare, BarChart2, ThumbsUp, ThumbsDown, Copy, Check, Trash2, Paperclip, Image, FileText, X } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import logoAsset from '../assets/logo-asset.png';
import { API_BASE_URL } from '../config/api';
import PremiumGuard from '../components/PremiumGuard';
import Swal from 'sweetalert2';

interface Message {
  id: number;
  role: string; // "user", "model", "system", "human"
  content: string;
  created_at: string;
  feedback?: string;
  attachment_url?: string;
  attachment_name?: string;
  attachment_type?: string;
}

interface AIAssistantPageProps {
  formData: any;
}

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const customUrlTransform = (url: string) => {
  const lowercase = url.trim().toLowerCase();
  if (
    lowercase.startsWith('http://') ||
    lowercase.startsWith('https://') ||
    lowercase.startsWith('mailto:') ||
    lowercase.startsWith('tel:') ||
    lowercase.startsWith('tradingview://') ||
    lowercase.startsWith('tradingview:')
  ) {
    return url;
  }
  return '';
};

const markdownComponents = {
  h1: ({node, ...props}: any) => <h1 className="text-xl font-black text-white mt-6 mb-4 border-b border-[#1C1D26] pb-2" {...props} />,
  h2: ({node, ...props}: any) => <h2 className="text-lg font-black text-white mt-5 mb-3 border-b border-[#1C1D26]/40 pb-1.5" {...props} />,
  h3: ({node, ...props}: any) => <h3 className="text-base font-bold text-white mt-4 mb-2" {...props} />,
  h4: ({node, ...props}: any) => <h4 className="text-sm font-bold text-white mt-3 mb-2" {...props} />,
  p: ({node, ...props}: any) => <p className="mb-3 text-neutral-300 font-sans leading-relaxed" {...props} />,
  ul: ({node, ...props}: any) => <ul className="list-disc pl-5 mb-3 space-y-1 text-neutral-300" {...props} />,
  ol: ({node, ...props}: any) => <ol className="list-decimal pl-5 mb-3 space-y-1 text-neutral-300" {...props} />,
  li: ({node, ...props}: any) => <li className="font-sans" {...props} />,
  strong: ({node, ...props}: any) => <strong className="font-bold text-white" {...props} />,
  em: ({node, ...props}: any) => <em className="italic text-neutral-400" {...props} />,
  table: ({node, ...props}: any) => <div className="overflow-x-auto mb-4 border border-[#1C1D26] rounded-lg"><table className="min-w-full text-sm text-left text-neutral-300" {...props} /></div>,
  thead: ({node, ...props}: any) => <thead className="text-xs text-neutral-400 uppercase bg-[#1a1a24] border-b border-[#1C1D26]" {...props} />,
  tbody: ({node, ...props}: any) => <tbody className="divide-y divide-[#1C1D26]" {...props} />,
  tr: ({node, ...props}: any) => <tr className="hover:bg-white/5" {...props} />,
  th: ({node, ...props}: any) => <th className="px-4 py-3 font-medium text-white" {...props} />,
  td: ({node, ...props}: any) => <td className="px-4 py-3" {...props} />,
  code: ({node, inline, className, children, ...props}: any) => {
    const match = /language-(\w+)/.exec(className || '');
    return !inline ? (
      <div className="rounded-xl overflow-hidden mb-4 border border-[#1C1D26]">
        <div className="bg-[#111] text-[10px] text-neutral-500 px-3 py-1.5 border-b border-[#1C1D26] uppercase font-bold tracking-wider">{match ? match[1] : 'Code'}</div>
        <pre className="bg-black p-4 overflow-x-auto text-xs text-neutral-300 font-mono">
          <code className={className} {...props}>{children}</code>
        </pre>
      </div>
    ) : (
      <code className="bg-[#1C1D26] text-[#E50914] px-1.5 py-0.5 rounded text-xs font-mono" {...props}>{children}</code>
    );
  },
  blockquote: ({node, ...props}: any) => <blockquote className="border-l-4 border-[#E50914] pl-4 italic my-4 text-neutral-400 bg-white/5 py-2 pr-4 rounded-r-lg" {...props} />,
  a: ({node, href, children, ...props}: any) => {
    const isTv = href && (href.startsWith('tradingview://') || href.startsWith('tradingview:') || href.includes('tradingview.com'));
    if (isTv) {
      let symbol = 'BINANCE:BTCUSDT';
      if (href.startsWith('tradingview://')) {
        symbol = href.replace('tradingview://', '');
      } else if (href.startsWith('tradingview:')) {
        symbol = href.replace('tradingview:', '');
      } else if (href.includes('symbol=')) {
        const match = href.match(/[?&]symbol=([^&]+)/);
        if (match) symbol = decodeURIComponent(match[1]);
      } else {
        const parts = href.split('/');
        const lastPart = parts[parts.length - 1] || parts[parts.length - 2];
        if (lastPart && lastPart.toUpperCase() === lastPart) {
          symbol = `BINANCE:${lastPart}`;
        }
      }
      symbol = symbol.trim().replace(/\/$/, '').toUpperCase();
      const theme = (typeof document !== 'undefined' && document.documentElement.classList.contains('light')) ? 'light' : 'dark';
      return (
        <div className="w-full my-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#E50914] mb-2 px-1">
            📈 {children || 'TradingView Chart'} ({symbol})
          </div>
          <div className="w-full h-[350px] rounded-2xl overflow-hidden border border-[#1C1D26] shadow-lg bg-black relative">
            <iframe
              src={`https://s.tradingview.com/widgetembed/?symbol=${symbol}&theme=${theme}&interval=D&hidesidetoolbar=1&symboledit=0&saveimage=0&toolbarbg=111111&timezone=Asia/Jakarta&locale=id`}
              width="100%"
              height="100%"
              frameBorder="0"
              allowFullScreen
              title={`TradingView Chart - ${symbol}`}
            />
          </div>
        </div>
      );
    }
    return <a className="text-[#E50914] hover:underline" href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
  }
};

import { useSEO } from '../hooks/useSEO';
import { useTheme } from '../hooks/useTheme';

const cleanContent = (text: string) => {
  if (!text) return '';
  const index = text.indexOf('\n\n[USER UPLOADED SPREADSHEET:');
  if (index !== -1) {
    return text.substring(0, index);
  }
  return text;
};

const AIAssistantPage: React.FC<AIAssistantPageProps> = ({ formData }) => {
  useSEO({
    title: 'Nera AI Assistant',
    description: 'Dapatkan feedback trading, analisis portofolio, dan wawasan pasar dari asisten trading AI Anda, Nera.',
    keywords: 'ai assistant, nera trading ai, trading chat, automated crypto advisor'
  });
  const isDarkMode = useTheme();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [thinkingStatus, setThinkingStatus] = useState('Nera AI sedang menghubungkan...');

  // File upload states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [attachedUrl, setAttachedUrl] = useState('');
  const [attachedBase64, setAttachedBase64] = useState('');
  const [attachedCsvContent, setAttachedCsvContent] = useState('');
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClearAttachment = () => {
    setSelectedFile(null);
    setAttachedUrl('');
    setAttachedBase64('');
    setAttachedCsvContent('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const processFile = async (file: File) => {
    setSelectedFile(file);
    setIsUploadingFile(true);

    try {
      // 1. Read file as base64
      const base64Promise = new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = error => reject(error);
        reader.readAsDataURL(file);
      });
      const base64Data = await base64Promise;
      setAttachedBase64(base64Data);

      // 2. Parse Excel/CSV if spreadsheet
      const isSpreadsheet = file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv') || file.type === 'text/csv' || file.type.includes('spreadsheet');
      if (isSpreadsheet) {
        if (file.name.endsWith('.csv')) {
          const textPromise = new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
            reader.readAsText(file);
          });
          const text = await textPromise;
          setAttachedCsvContent(text);
        } else {
          // XLSX / XLS
          const arrayBufferPromise = new Promise<ArrayBuffer>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as ArrayBuffer);
            reader.onerror = error => reject(error);
            reader.readAsArrayBuffer(file);
          });
          const buffer = await arrayBufferPromise;
          const XLSX = await import('xlsx');
          const data = new Uint8Array(buffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const csv = XLSX.utils.sheet_to_csv(worksheet);
          setAttachedCsvContent(csv);
        }
      }

      // 3. Upload file to backend /api/upload
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadResp = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (uploadResp.ok) {
        const uploadData = await uploadResp.json();
        if (uploadData.success && uploadData.url) {
          setAttachedUrl(uploadData.url);
        }
      } else {
        console.error('Failed to upload file to R2');
      }
    } catch (error) {
      console.error('Error processing uploaded file:', error);
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLInputElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          await processFile(file);
          break; // only capture first pasted image
        }
      }
    }
  };
  const [isHumanActive, setIsHumanActive] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [showAnalyticsDrawer, setShowAnalyticsDrawer] = useState(false);
  const [showMobileHistory, setShowMobileHistory] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);

  useEffect(() => {
    if (!isLoading) {
      setThinkingStatus('Nera AI sedang menghubungkan...');
      return;
    }

    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = (Date.now() - start) / 1000;
      if (elapsed >= 8.5) {
        setThinkingStatus('Nera AI sedang merumuskan rekomendasi...');
      } else if (elapsed >= 6.0) {
        setThinkingStatus('Nera AI sedang mendeteksi pola psikologi trading...');
      } else if (elapsed >= 3.5) {
        setThinkingStatus('Nera AI sedang menghitung metrik Sharpe & Expectancy...');
      } else if (elapsed >= 1.5) {
        setThinkingStatus('Nera AI sedang menganalisis jurnal transaksi...');
      } else {
        setThinkingStatus('Nera AI sedang menghubungkan...');
      }
    }, 500);

    return () => clearInterval(interval);
  }, [isLoading]);
  
  // ============================================================
  // NOTE: Human CS / live agent escalation (Slack, Zendesk, etc.)
  // is temporarily commented out. Support sementara via Discord:
  // discord.gg/catatcrypto — open ticket di sana.
  // Next step: integrasi platform helpdesk (Zendesk/Freshdesk)
  // ============================================================

  // --- COMMENTED OUT: CS escalation states ---
  // const [showEscalationSurvey, setShowEscalationSurvey] = useState(false);
  // const [selectedCategory, setSelectedCategory] = useState('');
  // const [surveyDetails, setSurveyDetails] = useState('');
  // const [surveyError, setSurveyError] = useState('');
  // const [isSubmittingSurvey, setIsSubmittingSurvey] = useState(false);
  // const [bypassHours, setBypassHours] = useState(false);
  // const [isHumanActive, setIsHumanActive] = useState(false);
  // const [agentName, setAgentName] = useState('');

  // Real Trades Stats fetched from backend
  const [stats, setStats] = useState({
    winRate: 0,
    totalTrades: 0,
    netPnl: 0,
    bestPair: 'N/A'
  });
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [activeSessionId, setActiveSessionId] = useState<string>('default');
  const [chatrooms, setChatrooms] = useState<{ session_id: string; title: string; updated_at: string }[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const token = localStorage.getItem('auth_token');

  // Load history and stats on mount
  useEffect(() => {
    if (token) {
      fetchHistory(activeSessionId);
      fetchTradeStats();
    }
  }, [token]);

  // Auto-scroll to bottom of chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // --- COMMENTED OUT: Real-time Human CS polling ---
  // useEffect(() => {
  //   if (!token || !isHumanActive) return;
  //   const interval = setInterval(() => { fetchHistorySilent(); }, 3000);
  //   return () => clearInterval(interval);
  // }, [token, isHumanActive, messages.length, activeSessionId]);


  const fetchTradeStats = async () => {
    try {
      setIsStatsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/trades`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (response.ok) {
        const resData = await response.json();
        const tradesList = resData.data || [];
        
        let total = tradesList.length;
        let wins = 0;
        let pnlSum = 0;
        let pairWinsMap: Record<string, number> = {};
        let best = 'N/A';
        let maxWins = 0;

        tradesList.forEach((t: any) => {
          const status = t.status || 'LOSS';
          if (status === 'WIN') {
            wins++;
            if (t.pairs) {
              pairWinsMap[t.pairs] = (pairWinsMap[t.pairs] || 0) + 1;
              if (pairWinsMap[t.pairs] > maxWins) {
                maxWins = pairWinsMap[t.pairs];
                best = t.pairs;
              }
            }
          }
          if (t.pnl_amount !== undefined && t.pnl_amount !== null) {
            pnlSum += t.pnl_amount;
          } else if (t.pnlAmount !== undefined && t.pnlAmount !== null) {
            pnlSum += t.pnlAmount;
          }
        });

        const wr = total > 0 ? Math.round((wins / total) * 100) : 0;

        setStats({
          winRate: wr,
          totalTrades: total,
          netPnl: pnlSum,
          bestPair: best
        });
      }
    } catch (err) {
      console.error('Failed to fetch trades stats:', err);
    } finally {
      setIsStatsLoading(false);
    }
  };

  const fetchHistory = async (sessId?: string) => {
    try {
      const targetId = sessId || activeSessionId;
      const response = await fetch(`${API_BASE_URL}/api/neo/history?session_id=${targetId}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.data || []);
        // setIsHumanActive(data.is_human_active); // commented out: human CS
        // setAgentName(data.agent_name || '');      // commented out: human CS
        if (data.session_id) setActiveSessionId(data.session_id);
        if (data.rooms) setChatrooms(data.rooms);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const fetchHistorySilent = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/neo/history?session_id=${activeSessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.data && data.data.length !== messages.length) {
          setMessages(data.data);
        }
        setIsHumanActive(data.is_human_active);
        setAgentName(data.agent_name || '');
        if (data.rooms) {
          setChatrooms(data.rooms);
        }
      }
    } catch (error) {
      // Fail silently
    }
  };

  const handleSendMessage = async (textToSend: string) => {
    const text = textToSend.trim();
    if (!text && !attachedUrl) return;
    if (isLoading || isStreaming || !token) return;

    const fileUrl = attachedUrl;
    const fileName = selectedFile?.name || '';
    const fileType = selectedFile?.type || '';
    const fileBase64 = attachedBase64;
    const fileCsvContent = attachedCsvContent;

    setInputMessage('');
    handleClearAttachment();
    setIsLoading(true);

    // Optimistically insert user's message
    const tempUserMsg: Message = {
      id: Date.now(),
      role: 'user',
      content: text,
      attachment_url: fileUrl,
      attachment_name: fileName,
      attachment_type: fileType,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const response = await fetch(`${API_BASE_URL}/api/neo/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify({
          message: text,
          session_id: activeSessionId,
          attachment_url: fileUrl,
          attachment_name: fileName,
          attachment_type: fileType,
          attachment_base64: fileBase64,
          attachment_csv_content: fileCsvContent
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMsg: Message = {
          id: Date.now() + 1,
          role: 'system',
          content: `Error: ${errorData.message || 'Failed to send message'}`,
          created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMsg]);
        return;
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('text/event-stream')) {
        // --- SSE STREAMING MODE (Ollama) ---
        setStreamingText('');

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let accumulated = '';
        let hasReceivedFirstToken = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              if (data === '[DONE]') break;
              try {
                const parsed = JSON.parse(data);
                if (parsed.token) {
                  if (!hasReceivedFirstToken) {
                    hasReceivedFirstToken = true;
                    setIsLoading(false);
                    setIsStreaming(true);
                  }
                  accumulated += parsed.token;
                  setStreamingText(accumulated);
                } else if (parsed.done) {
                  await fetchHistory(activeSessionId);
                  setStreamingText('');
                }
              } catch (_) { /* ignore parse errors */ }
            }
          }
        }

        setIsStreaming(false);
        setStreamingText('');
        await fetchHistory(activeSessionId);
      } else {
        // --- REGULAR JSON MODE (Gemini) ---
        const data = await response.json();
        const fullMessage = data.data;
        setIsLoading(false);
        
        if (fullMessage && (fullMessage.role === 'model' || fullMessage.role === 'human')) {
          const words = fullMessage.content.split(' ');
          let currentContent = '';
          let wordIndex = 0;

          const streamingId = Date.now() + 999;
          const tempStreamingMsg: Message = {
            id: streamingId,
            role: fullMessage.role,
            content: '',
            created_at: fullMessage.created_at
          };

          setMessages(prev => [...prev, tempStreamingMsg]);

          const timer = setInterval(() => {
            if (wordIndex < words.length) {
              currentContent += (wordIndex === 0 ? '' : ' ') + words[wordIndex];
              setMessages(prev => 
                prev.map(m => m.id === streamingId ? { ...m, content: currentContent } : m)
              );
              wordIndex++;
            } else {
              clearInterval(timer);
              fetchHistory(activeSessionId);
            }
          }, 20);
        } else {
          fetchHistory(activeSessionId);
        }
      }
    } catch (error) {
      const networkErrorMsg: Message = {
        id: Date.now() + 1,
        role: 'system',
        content: 'Error: Failed to contact backend server.',
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, networkErrorMsg]);
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
    }
  };

  const handleResetSession = async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/neo/session/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (response.ok) {
        fetchHistory(activeSessionId);
      }
    } catch (error) {
      console.error('Failed to reset session:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewChatroom = () => {
    const newSessId = "session-" + Date.now() + "-" + Math.random().toString(36).substring(2, 7);
    setActiveSessionId(newSessId);
    setMessages([]); // Optimistically clear messages for seamless UI
    fetchHistory(newSessId);
    setShowMobileHistory(false);
  };

  const handleSuggestionClick = (query: string) => {
    handleSendMessage(query);
  };

  // --- COMMENTED OUT: Human CS escalation handler ---
  // const handleSendEscalation = async () => { ... };

  const handleSaveFeedback = async (messageId: number, feedbackType: 'like' | 'dislike') => {
    if (!token) return;
    
    // Find the current message to toggle
    const currentMsg = messages.find(m => m.id === messageId);
    const newFeedback = currentMsg?.feedback === feedbackType ? '' : feedbackType;

    // Optimistically update the UI
    setMessages(prev => prev.map(m => m.id === messageId ? { ...m, feedback: newFeedback } : m));

    try {
      const response = await fetch(`${API_BASE_URL}/api/neo/message/${messageId}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ feedback: newFeedback })
      });

      if (!response.ok) {
        // Rollback on error
        setMessages(prev => prev.map(m => m.id === messageId ? { ...m, feedback: currentMsg?.feedback || '' } : m));
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      // Rollback on error
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, feedback: currentMsg?.feedback || '' } : m));
    }
  };

  const handleCopyToClipboard = (messageId: number, content: string) => {
    navigator.clipboard.writeText(content).then(() => {
      setCopiedMessageId(messageId);
      setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy text:', err);
    });
  };

  const handleDeleteChat = async (sessionId: string) => {
    if (!token) return;
    
    const result = await Swal.fire({
      title: 'Delete Chat History?',
      text: 'This action cannot be undone. All messages in this conversation will be permanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
      background: isDarkMode ? '#0c0c0f' : '#ffffff',
      color: isDarkMode ? '#ffffff' : '#000000',
      customClass: {
        popup: 'rounded-2xl border ' + (isDarkMode ? 'border-neutral-800' : 'border-gray-200'),
        title: 'text-lg font-bold ' + (isDarkMode ? 'text-white' : 'text-black'),
        htmlContainer: 'text-sm ' + (isDarkMode ? 'text-neutral-400' : 'text-gray-600'),
        confirmButton: 'rounded-xl font-bold px-6 py-2.5 text-sm',
        cancelButton: 'rounded-xl font-bold px-6 py-2.5 text-sm'
      }
    });

    if (!result.isConfirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/neo/history/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        // Remove from state
        setChatrooms(prev => prev.filter(room => room.session_id !== sessionId));
        
        // If deleted chat was active, reset to default
        if (activeSessionId === sessionId) {
          setActiveSessionId('default');
          fetchHistory('default');
        }

        // Success notification
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Chat history has been deleted successfully.',
          timer: 2000,
          showConfirmButton: false,
          background: isDarkMode ? '#0c0c0f' : '#ffffff',
          color: isDarkMode ? '#ffffff' : '#000000',
          customClass: {
            popup: 'rounded-2xl border ' + (isDarkMode ? 'border-neutral-800' : 'border-gray-200')
          }
        });
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      console.error('Failed to delete chat:', error);
      
      // Error notification
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: 'Could not delete chat history. Please try again.',
        confirmButtonColor: '#ef4444',
        background: isDarkMode ? '#0c0c0f' : '#ffffff',
        color: isDarkMode ? '#ffffff' : '#000000',
        customClass: {
          popup: 'rounded-2xl border ' + (isDarkMode ? 'border-neutral-800' : 'border-gray-200'),
          confirmButton: 'rounded-xl font-bold px-6 py-2.5 text-sm'
        }
      });
    }
  };

  // --- COMMENTED OUT: handleSendEscalation (human CS via API) ---
  // Replace with: direct Discord link at discord.gg/catatcrypto

  // Chatrooms are fetched dynamically from backend
  const hasChatrooms = chatrooms && chatrooms.length > 0;

  return (
    <div className={`h-screen h-[100dvh] flex overflow-hidden font-sans ${isDarkMode ? 'bg-black text-white' : 'bg-[#FAFAFA] text-black'}`}>
      {/* Left Navigation Sidebar */}
      <Sidebar plan={formData.plan} />

      {/* Main Canvas Area */}
      <PremiumGuard requiredPlan="pro">
        <main className="flex-1 bg-[#111111] h-screen h-[100dvh] flex flex-col overflow-hidden pt-16 md:pt-0">
        
        {/* Top bar */}
        <TopBar name={formData.name} title="AI Assistant" showAccountSwitcher={true} />

        {/* Immersive Dual Panel ChatGPT/Claude Layout */}
        <div className="flex-1 bg-[#111111] overflow-hidden flex">
          
          <div className="flex-1 bg-[#111111] border-t border-[#1C1D26]/50 overflow-hidden flex h-full">
            
            {/* ================= 1. LEFT SIDEBAR: CHAT HISTORY (ChatGPT STYLE) ================= */}
            {/* Backdrop overlay for mobile */}
            {showMobileHistory && (
              <div
                className="fixed inset-0 bg-black/60 z-40 md:hidden"
                onClick={() => setShowMobileHistory(false)}
              />
            )}

            <div className={`fixed md:static inset-y-0 left-0 z-50 w-64 flex flex-col h-full
              transform transition-transform duration-300 ease-in-out
              ${showMobileHistory ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
              ${isDarkMode 
                ? 'bg-[#0c0c0f] border-r border-[#1C1D26]/50' 
                : 'bg-white border-r border-gray-200'
            }`}>
              
              {/* New Chat Button + Mobile Quick Action */}
              <div className="p-3.5 space-y-2">
                <button
                  onClick={handleNewChatroom}
                  className={`w-full rounded-xl py-2 px-3.5 text-[11px] font-bold tracking-wide uppercase transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-[0.98] ${
                    isDarkMode
                      ? 'bg-[#111111] hover:bg-[#1a1a24] border border-[#1C1D26]/60 text-neutral-300'
                      : 'bg-gray-100 hover:bg-gray-200 border border-gray-200 text-gray-700'
                  }`}
                >
                  <Plus size={12} className="text-[#E50914]" />
                  <span>Percakapan Baru</span>
                </button>

                {/* Analitis Portofolio — dipindah dari header ke sini khusus mobile */}
                <button
                  onClick={() => {
                    setShowAnalyticsDrawer(true);
                    setShowMobileHistory(false);
                  }}
                  className={`md:hidden w-full rounded-xl py-2 px-3.5 text-[11px] font-bold tracking-wide uppercase transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-[0.98] ${
                    isDarkMode
                      ? 'bg-[#1C1C24] hover:bg-[#252530] border border-[#1C1D26]/60 text-neutral-300 hover:text-white'
                      : 'bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 hover:text-black'
                  }`}
                >
                  <BarChart2 size={12} className="text-[#E50914]" />
                  <span>Analitis Portofolio</span>
                </button>
              </div>

              {/* Chat history thread list */}
              <div className="flex-1 overflow-y-auto px-3.5 pb-4 space-y-1 scrollbar-none" data-lenis-prevent>
                <span className={`text-[9px] font-black tracking-widest uppercase block px-2.5 py-2 ${
                  isDarkMode ? 'text-neutral-600' : 'text-gray-500'
                }`}>Riwayat Diskusi</span>
                
                {!hasChatrooms ? (
                  <div className={`px-3.5 py-4 text-center rounded-xl ${
                    isDarkMode 
                      ? 'border border-dashed border-[#1C1D26]/30' 
                      : 'border border-dashed border-gray-200'
                  }`}>
                    <p className={`text-[10px] leading-normal ${isDarkMode ? 'text-neutral-600' : 'text-gray-500'}`}>Mulai kirim pesan untuk menyimpan riwayat chat di sini.</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {chatrooms.map((room, idx) => (
                      <div
                        key={idx}
                        className="relative group"
                      >
                        <button
                          onClick={() => {
                            setActiveSessionId(room.session_id);
                            fetchHistory(room.session_id);
                            setShowMobileHistory(false);
                          }}
                          className={`w-full text-left truncate pl-3 pr-9 py-2.5 rounded-xl text-[10px] cursor-pointer transition-all flex items-center gap-2 ${
                            room.session_id === activeSessionId
                              ? isDarkMode
                                ? 'bg-[#1C1C24] border border-[#1C1D26] text-white font-semibold shadow-sm'
                                : 'bg-red-50 border border-red-100 text-black font-semibold'
                              : isDarkMode
                                ? 'text-neutral-400 hover:text-white hover:bg-neutral-900/60 border border-transparent hover:border-[#1C1D26]/20'
                                : 'text-gray-600 hover:text-black hover:bg-gray-50 border border-transparent hover:border-gray-200'
                          }`}
                        >
                          <MessageSquare size={10} className={`shrink-0 ${
                            room.session_id === activeSessionId 
                              ? 'text-[#E50914]' 
                              : isDarkMode ? 'text-neutral-600 group-hover:text-[#E50914]' : 'text-gray-400 group-hover:text-[#E50914]'
                          }`} />
                          <span className="truncate flex-1">{room.title}</span>
                        </button>
                        
                        {/* Trash icon — selalu tampil, tanpa hover gating */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteChat(room.session_id);
                          }}
                          className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-all ${
                            isDarkMode
                              ? 'bg-red-950/40 hover:bg-red-950/60 text-red-400 hover:text-red-300'
                              : 'bg-red-50 hover:bg-red-100 text-red-500 hover:text-red-600'
                          }`}
                          title="Delete chat"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Left sidebar footer - status info */}
              <div className={`p-4 flex items-center justify-between ${
                isDarkMode 
                  ? 'border-t border-[#1C1D26]/30 bg-[#08080a]' 
                  : 'border-t border-gray-200 bg-gray-50'
              }`}>
                <span className={`text-[9px] font-bold uppercase ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>Status Sistem</span>
                <div className="flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-emerald-500" />
                  <span className="text-[8px] font-bold text-emerald-500 uppercase">Aktif</span>
                </div>
              </div>

            </div>

            {/* ================= 2. RIGHT SIDEBAR: ACTIVE CHAT PANEL ================= */}
            <div className={`flex-1 flex flex-col h-full relative overflow-hidden ${isDarkMode ? 'bg-[#111111]' : 'bg-white'}`}>
              
              {/* Chat workspace header */}
              <div className={`p-4 flex items-center justify-between ${
                isHumanActive 
                  ? 'bg-green-950/10 border-b border-[#1C1D26]/50' 
                  : isDarkMode
                    ? 'bg-black/10 border-b border-[#1C1D26]/50'
                    : 'bg-gray-50 border-b border-gray-200'
              }`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {isHumanActive ? (
                    <div className="relative">
                      <div className="w-9 h-9 rounded-xl bg-green-950/40 border border-green-500/20 flex items-center justify-center text-green-400">
                        <Headphones size={18} />
                      </div>
                      <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-500 border border-black rounded-full animate-ping" />
                      <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-500 border border-black rounded-full" />
                    </div>
                  ) : (
                    <div className="w-9 h-9 rounded-xl bg-[#1A0605] border border-[#3A1410] flex items-center justify-center p-1.5 shadow-sm select-none">
                      <img src={logoAsset} alt="CatatCrypto" className="w-full h-full object-contain" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className={`text-xs font-bold uppercase tracking-wider truncate ${isDarkMode ? 'text-white' : 'text-black'}`}>
                      {isHumanActive ? `Live Chat: ${agentName} (Support)` : 'Nera AI'}
                    </h3>
                    <p className={`text-[9px] font-sans mt-0.5 flex items-center gap-1 min-w-0 ${isDarkMode ? 'text-neutral-500' : 'text-gray-600'}`}>
                      {isHumanActive ? (
                        <span className="text-green-400 font-bold uppercase tracking-widest text-[8px] truncate">Live Connection Active</span>
                      ) : (
                        <span className="truncate">Konsultasi Analisis & Dukungan Akun</span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Right Header Action Menu */}
                <div className="flex items-center gap-2 flex-nowrap shrink-0">
                  <button
                    onClick={() => setShowMobileHistory(true)}
                    className="md:hidden p-2 rounded-xl bg-[#1C1C24] border border-[#1C1D26]/50 text-neutral-300"
                    title="Riwayat Diskusi"
                  >
                    <MessageSquare size={14} />
                  </button>

                  <button
                    onClick={() => {
                      setShowAnalyticsDrawer(!showAnalyticsDrawer);
                      setShowMobileHistory(false);
                    }}
                    className={`hidden md:flex font-bold text-[9px] uppercase tracking-wider py-1.5 px-3 rounded-xl transition-all items-center gap-1.5 cursor-pointer active:scale-95 ${
                      isDarkMode
                        ? 'bg-[#1C1C24] hover:bg-[#252530] border border-[#1C1D26]/50 text-neutral-300 hover:text-white'
                        : 'bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 hover:text-black'
                    }`}
                  >
                    <BarChart2 size={10} className="text-[#E50914]" />
                    <span>Analitis Portofolio</span>
                  </button>

                  {isHumanActive && (
                    <button
                      onClick={handleResetSession}
                      disabled={isLoading}
                      className={`font-bold text-[9px] uppercase tracking-wider py-1.5 px-3 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer ${
                        isDarkMode
                          ? 'bg-[#1C1C24] hover:bg-[#252530] border border-[#1C1D26]/50 text-neutral-400 hover:text-white disabled:opacity-50'
                          : 'bg-white hover:bg-gray-50 border border-gray-200 text-gray-600 hover:text-black disabled:opacity-50'
                      }`}
                    >
                      <RefreshCw size={10} className={isLoading ? 'animate-spin' : ''} />
                      <span>Akhiri Live Support</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Chat Thread Messages Area */}
              <div className={`flex-1 overflow-y-auto p-6 space-y-6 scrollbar-thin scrollbar-track-transparent ${
                isDarkMode ? 'scrollbar-thumb-neutral-900' : 'scrollbar-thumb-gray-200'
              }`} data-lenis-prevent>
                {messages.length === 0 && !isLoading && (
                  <div className="flex justify-start">
                    <div className="flex gap-3 max-w-[85vw] md:max-w-2xl flex-row items-start">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs border bg-neutral-950 text-[#E50914] border-[#1C1D26]">
                        <img src={logoAsset} alt="CatatCrypto" className="w-3.5 h-3.5 object-contain select-none" />
                      </div>
                      <div className="flex-1 max-w-full">
                        <div className="p-0 sm:p-2 text-xs leading-relaxed font-sans bg-transparent text-neutral-200">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents} urlTransform={customUrlTransform}>
                            {"Halo! Saya Nera AI, asisten portofolio CatatCrypto Anda. Saya dapat membantu menganalisis performa trading, mengevaluasi konsistensi jurnal, menjelaskan fitur platform, atau memberikan tips psikologi trading.\n\nAda yang bisa saya bantu hari ini?"}
                          </ReactMarkdown>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {messages.map((msg, i) => {
                  const isUser = msg.role === 'user';
                  const isSystem = msg.role === 'system';
                  const isHuman = msg.role === 'human';

                  if (isSystem) {
                    return (
                      <div key={msg.id || i} className="flex justify-center my-4 select-none">
                        <div className="bg-black/50 border border-[#1C1D26]/40 rounded-2xl py-2.5 px-4 flex items-center gap-2.5 max-w-xl">
                          <AlertCircle size={12} className="text-amber-500 flex-shrink-0" />
                          <span className="text-[10px] font-sans text-neutral-400 leading-normal">{msg.content}</span>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={msg.id || i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                      <div className={`flex gap-3 max-w-[85vw] md:max-w-2xl ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
                        
                        {/* Avatar */}
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs border ${
                          isUser 
                            ? 'bg-neutral-950 text-neutral-400 border-[#1C1D26]' 
                            : isHuman 
                            ? 'bg-green-950/40 text-green-400 border-green-900/30' 
                            : 'bg-neutral-950 text-[#E50914] border-[#1C1D26]'
                        }`}>
                          {isUser ? <User size={10} /> : isHuman ? <Headphones size={10} /> : <img src={logoAsset} alt="CatatCrypto" className="w-3.5 h-3.5 object-contain select-none" />}
                        </div>

                        {/* Bubble */}
                        <div className="flex-1 max-w-full">
                          <div className={`p-0 sm:p-2 text-xs leading-relaxed font-sans ${
                            isUser
                              ? 'p-4 bg-[#220D0A] border border-[#4A1410] text-neutral-100 rounded-3xl rounded-tr-none shadow-sm'
                              : isHuman
                              ? 'bg-transparent text-neutral-200'
                              : 'bg-transparent text-neutral-200'
                          }`}>
                            {isUser ? (
                              <div>
                                <p className="mb-0 text-white font-sans leading-relaxed">{cleanContent(msg.content)}</p>
                                {msg.attachment_url && msg.attachment_type?.startsWith('image/') && (
                                  <div className="mt-2 rounded-xl overflow-hidden max-w-xs border border-white/10 shadow-lg">
                                    <img 
                                      src={msg.attachment_url} 
                                      alt={msg.attachment_name} 
                                      className="w-full max-h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity" 
                                      onClick={() => window.open(msg.attachment_url, '_blank')} 
                                    />
                                  </div>
                                )}
                                {msg.attachment_url && !msg.attachment_type?.startsWith('image/') && (
                                  <div className="mt-2">
                                    <a
                                      href={msg.attachment_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-2 p-2 rounded-xl bg-black/40 border border-white/10 hover:border-white/20 hover:bg-black/60 text-[10px] text-white transition-all"
                                    >
                                      <Paperclip size={10} className="text-white/80" />
                                      <span className="truncate max-w-[150px] font-medium">{msg.attachment_name || 'Attached File'}</span>
                                    </a>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents} urlTransform={customUrlTransform}>
                                {msg.content}
                              </ReactMarkdown>
                            )}
                          </div>
                          
                          <div className="flex items-center justify-between mt-1.5 px-1 select-none">
                            <span className="text-[8px] text-neutral-600 font-sans">
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            
                            {/* Like / Dislike / Copy actions row for AI/Human responses */}
                            {!isUser && (
                              <div className="flex items-center gap-2 text-neutral-500">
                                <button
                                  onClick={() => handleSaveFeedback(msg.id, 'like')}
                                  title="Like"
                                  className={`p-1 rounded hover:bg-neutral-900 hover:text-green-400 transition-all cursor-pointer active:scale-90 ${
                                    msg.feedback === 'like' ? 'text-green-500 bg-green-950/20' : ''
                                  }`}
                                >
                                  <ThumbsUp size={11} className={msg.feedback === 'like' ? 'fill-green-500' : ''} />
                                </button>
                                
                                <button
                                  onClick={() => handleSaveFeedback(msg.id, 'dislike')}
                                  title="Dislike"
                                  className={`p-1 rounded hover:bg-neutral-900 hover:text-red-400 transition-all cursor-pointer active:scale-90 ${
                                    msg.feedback === 'dislike' ? 'text-red-500 bg-red-950/20' : ''
                                  }`}
                                >
                                  <ThumbsDown size={11} className={msg.feedback === 'dislike' ? 'fill-red-500' : ''} />
                                </button>
                                
                                <button
                                  onClick={() => handleCopyToClipboard(msg.id, msg.content)}
                                  title="Salin ke clipboard"
                                  className={`p-1 rounded hover:bg-neutral-900 hover:text-white transition-all cursor-pointer flex items-center gap-1 active:scale-90 ${
                                    copiedMessageId === msg.id ? 'text-emerald-400 bg-emerald-950/20 px-1.5' : ''
                                  }`}
                                >
                                  {copiedMessageId === msg.id ? (
                                    <>
                                      <Check size={11} />
                                      <span className="text-[7.5px] font-sans font-black uppercase tracking-widest text-emerald-400">Copied!</span>
                                    </>
                                  ) : (
                                    <Copy size={11} />
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {isLoading && (
                  <div className="flex justify-start">
                    <div className="flex gap-3 max-w-[85vw] md:max-w-2xl flex-row items-start">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs border bg-neutral-950 text-[#E50914] border-[#1C1D26] mt-0.5">
                        <img src={logoAsset} alt="CatatCrypto" className="w-3.5 h-3.5 object-contain select-none" />
                      </div>
                      <div>
                        <div className="p-2 sm:p-4 text-xs leading-relaxed font-sans bg-transparent text-neutral-300 flex flex-col gap-2">
                          <span className="text-[11px] text-neutral-400 font-sans leading-normal animate-pulse">
                            {thinkingStatus}
                          </span>
                          <div className="flex gap-1 items-center">
                            <span className="w-1.5 h-1.5 bg-[#E50914] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-1.5 h-1.5 bg-[#E50914] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-1.5 h-1.5 bg-[#E50914] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isStreaming && streamingText && (
                  <div className="flex justify-start">
                    <div className="flex gap-3 max-w-[85vw] md:max-w-2xl flex-row items-start">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs border bg-neutral-950 text-[#E50914] border-[#1C1D26]">
                        <img src={logoAsset} alt="CatatCrypto" className="w-3.5 h-3.5 object-contain select-none" />
                      </div>
                      <div>
                        <div className="p-2 sm:p-4 text-xs leading-relaxed font-sans bg-transparent text-neutral-200">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents} urlTransform={customUrlTransform}>
                            {streamingText + ' ▋'}
                          </ReactMarkdown>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>


              {/* Suggestions chips */}
              {messages.length <= 1 && (
                <div className="p-3 md:p-4 bg-black/20 border-t border-[#1C1D26]/30 flex flex-wrap gap-2">
                  <button
                    onClick={() => handleSuggestionClick('Audit total Win Rate trading dan performa PnL portofolio saya')}
                    className="bg-black hover:bg-neutral-900 border border-[#1C1D26]/50 hover:border-neutral-700 text-[10px] text-neutral-400 hover:text-white px-3 py-1.5 rounded-full cursor-pointer transition-all"
                  >
                    Audit Portofolio Trading
                  </button>
                  <button
                    onClick={() => handleSuggestionClick('Berikan evaluasi psikologi trading dan tips mengendalikan overtrading')}
                    className="bg-black hover:bg-neutral-900 border border-[#1C1D26]/50 hover:border-neutral-700 text-[10px] text-neutral-400 hover:text-white px-3 py-1.5 rounded-full cursor-pointer transition-all"
                  >
                    Evaluasi Overtrading
                  </button>
                  <button
                    onClick={() => handleSuggestionClick('Jelaskan detail perbandingan plan Free vs Pro vs Premium')}
                    className="bg-black hover:bg-neutral-900 border border-[#1C1D26]/50 hover:border-neutral-700 text-[10px] text-neutral-400 hover:text-white px-3 py-1.5 rounded-full cursor-pointer transition-all"
                  >
                    Bandingkan Paket
                  </button>
                  {/* Discord support chips — replaces old 'Hubungi Live Support' */}
                  <a
                    href="/faq"
                    className="bg-black hover:bg-neutral-900 border border-[#1C1D26]/50 hover:border-neutral-700 text-[10px] text-neutral-400 hover:text-white px-3 py-1.5 rounded-full cursor-pointer transition-all"
                  >
                    ❓ Lihat FAQ
                  </a>
                  <a
                    href="https://discord.gg/nshaJqNA4X"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#5865F2]/10 hover:bg-[#5865F2]/20 border border-[#5865F2]/30 text-[10px] text-[#7289da] hover:text-white px-3 py-1.5 rounded-full cursor-pointer transition-all flex items-center gap-1"
                  >
                    💬 Discord Support
                  </a>
                </div>
              )}

              {/* Discord footer */}
              <div className="px-4 py-2 bg-[#5865F2]/5 border-t border-[#5865F2]/10 flex items-center justify-between">
                <span className="text-[9px] text-neutral-600">Butuh bantuan lebih?</span>
                <a
                  href="https://discord.gg/nshaJqNA4X"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[9px] font-black text-[#7289da] hover:text-white flex items-center gap-1 transition-colors"
                >
                  💬 Buka Tiket di Discord
                </a>
              </div>
                        {/* Selected attachment preview */}
              {(selectedFile || isUploadingFile) && (
                <div className="px-4 py-2 border-t border-[#1C1D26]/50 bg-black/95 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-neutral-900 border border-neutral-800 flex items-center justify-center text-neutral-400">
                      {isUploadingFile ? (
                        <RefreshCw size={12} className="animate-spin text-red-500" />
                      ) : selectedFile?.type.startsWith('image/') ? (
                        <Image size={12} className="text-red-500" />
                      ) : (
                        <FileText size={12} className="text-red-500" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] text-white font-medium truncate max-w-[200px]">
                        {selectedFile?.name || 'Uploading...'}
                      </p>
                      <p className="text-[8px] text-neutral-500">
                        {isUploadingFile ? 'Uploading to storage...' : 'File attached'}
                      </p>
                    </div>
                  </div>
                  {!isUploadingFile && (
                    <button
                      onClick={handleClearAttachment}
                      className="p-1 rounded-full hover:bg-neutral-800 text-neutral-400 hover:text-white transition-colors cursor-pointer"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              )}

              {/* Message Input Bar */}
              <div className="p-3 md:p-4 border-t border-[#1C1D26]/30 bg-[#111111]/85 backdrop-blur-md sticky bottom-0 z-10 w-full">
                <div className="max-w-2xl mx-auto w-full flex flex-col">
                  
                  {/* Unified input container (Pill shape style) */}
                  <div className="flex items-center gap-2 px-3 py-2 bg-[#0c0c0e] border border-[#1C1D26]/60 hover:border-neutral-700 focus-within:border-neutral-600 rounded-[24px] shadow-lg transition-all">
                    
                    {/* File attachment hidden input */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      accept="image/*,application/pdf,.csv,.xlsx,.xls"
                      className="hidden"
                    />

                    {/* Paperclip button */}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isLoading || isUploadingFile}
                      className="w-8 h-8 rounded-full hover:bg-neutral-800 text-neutral-400 hover:text-white flex items-center justify-center cursor-pointer transition-all active:scale-95 disabled:opacity-50 shrink-0"
                      title="Attach file (Image, PDF, Excel, CSV)"
                    >
                      <Paperclip size={15} />
                    </button>

                    {/* TextInput */}
                    <input
                      type="text"
                      value={inputMessage}
                      onChange={e => setInputMessage(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSendMessage(inputMessage); }}
                      onPaste={handlePaste}
                      disabled={isLoading || isUploadingFile}
                      placeholder={isUploadingFile ? "Processing file..." : "Tanyakan analisis performa trading atau cara pakai CatatCrypto..."}
                      className="flex-1 bg-transparent border-none focus:outline-none py-1.5 px-1 text-xs text-white placeholder-neutral-600 disabled:opacity-50 font-sans"
                    />

                    {/* Send button (Sleek Circle GPT/Gemini Style) */}
                    <button
                      onClick={() => handleSendMessage(inputMessage)}
                      disabled={isLoading || isUploadingFile || (!inputMessage.trim() && !attachedUrl)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-all cursor-pointer shrink-0 ${
                        inputMessage.trim() || attachedUrl
                          ? 'bg-[#E50914] text-white active:scale-95 shadow-md shadow-red-950/20'
                          : 'bg-neutral-900 text-neutral-600 cursor-not-allowed'
                      }`}
                      title="Send message"
                    >
                      <Send size={12} className={inputMessage.trim() || attachedUrl ? 'translate-x-[0.5px] -translate-y-[0.5px]' : ''} />
                    </button>
                  </div>
                  
                  {/* Subtle caption beneath input */}
                  <span className="text-[8px] text-neutral-600 font-sans mt-1.5 text-center select-none">
                    Nera AI dapat membuat kesalahan. Periksa kembali informasi penting.
                  </span>
                </div>
              </div>

              {/* ================= 3. SLIDING PORTFOLIO ANALYTICS DRAWER ================= */}
              {showAnalyticsDrawer && (
                <div className="absolute right-0 top-0 bottom-0 w-full md:w-80 bg-black/95 border-l border-[#1C1D26] p-4 md:p-6 shadow-2xl z-40 backdrop-blur-xl transition-all duration-300 overflow-y-auto">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-[11px] font-extrabold uppercase tracking-widest text-neutral-400">Analitis Portofolio</span>
                    <button 
                      onClick={() => setShowAnalyticsDrawer(false)}
                      className="text-neutral-500 hover:text-white font-bold text-xs"
                    >
                      Tutup ×
                    </button>
                  </div>
                  
                  <div className="space-y-5">
                    {/* Connection status */}
                    <div className="bg-[#111111] border border-[#1C1D26]/70 rounded-2xl p-4">
                      <span className="text-[9px] text-neutral-500 uppercase font-semibold">Integrasi Jurnal</span>
                      <div className="flex items-center gap-2 mt-1 select-none">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        <span className="text-[10px] font-bold text-emerald-400 uppercase">TERHUBUNG & AKTIF</span>
                      </div>
                    </div>

                    {/* Trade stats numbers */}
                    <div className="grid grid-cols-2 gap-3.5">
                      <div className="bg-[#111111] border border-[#1C1D26]/70 rounded-2xl p-4">
                        <span className="text-[9px] text-neutral-500 uppercase font-semibold">Win Rate</span>
                        <p className="text-xl font-bold text-white tracking-tight mt-1">{isStatsLoading ? '...' : `${stats.winRate}%`}</p>
                      </div>
                      <div className="bg-[#111111] border border-[#1C1D26]/70 rounded-2xl p-4">
                        <span className="text-[9px] text-neutral-500 uppercase font-semibold">Total Trades</span>
                        <p className="text-xl font-bold text-white tracking-tight mt-1">{isStatsLoading ? '...' : stats.totalTrades}</p>
                      </div>
                      <div className="bg-[#111111] border border-[#1C1D26]/70 rounded-2xl p-4 col-span-2">
                        <span className="text-[9px] text-neutral-500 uppercase font-semibold">Net Profit / Loss</span>
                        <p className={`text-lg font-black tracking-tight mt-1 ${stats.netPnl >= 0 ? 'text-emerald-500' : 'text-[#E50914]'}`}>
                          {isStatsLoading ? '...' : (stats.netPnl >= 0 ? `+$${stats.netPnl.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `-$${Math.abs(stats.netPnl).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)}
                        </p>
                      </div>
                      {stats.bestPair !== 'N/A' && (
                        <div className="bg-[#111111] border border-[#1C1D26]/70 rounded-2xl p-4 col-span-2">
                          <span className="text-[9px] text-neutral-500 uppercase font-semibold">Best Asset</span>
                          <p className="text-sm font-bold text-white tracking-tight mt-1 uppercase">{stats.bestPair}</p>
                        </div>
                      )}
                    </div>

                    {/* Explanatory footer */}
                    <div className="bg-[#111111]/50 border border-[#1C1D26]/30 rounded-2xl p-4.5">
                      <p className="text-[10px] text-neutral-500 leading-relaxed">
                        The above analytical data is retrieved in real-time from the transaction database successfully synced to CatatCrypto.
                      </p>
                    </div>
                  </div>
                </div>
              )}

            </div>

          </div>

        </div>
        </main>
      </PremiumGuard>
    </div>
  );
};

export default AIAssistantPage;
